import * as Yup from "yup";
import {
  applicationFieldErrorMessage,
  businessUnitErrorMessage,
  dropdownRequiredErrorMessage,
  inputFormatErrorMessage,
  marketSegmentErrorMessage,
} from "../../../../../helper/YupValidationErrors";
import { validPositiveBigDecimal, validPositiveInteger } from "../../../../../helper/YupValidationHelpers";
import * as FieldNames from "../fieldNames";
import ProductReferenceResponse, {
  ProductReferenceResponseWithType,
} from "../../../../../api/models/ProductReferenceResponse";
import TestRequestPurpose from "../../../../../api/models/test-request/TestRequestPurpose";

function optionalIfCrmLinked(linked: boolean, requiredMessage: string): Yup.StringSchema<string | null> {
  return linked ? Yup.string().nullable() : Yup.string().required(requiredMessage);
}

export default function getValidationSchemaSales(
  testRequestStatus: string,
  purpose: TestRequestPurpose,
  linkedWithOppId: boolean,
) {
  const testPurpose =
    testRequestStatus === "DRAFT" ? Yup.string().required(dropdownRequiredErrorMessage) : Yup.string().nullable();

  const testEffortInDays =
    testRequestStatus === "FOR_EVALUATION" && purpose !== "APPLICATION_PROCESS"
      ? Yup.string().required("Test effort in days is required")
      : Yup.string().nullable();

  const estimatedFinishingDate =
    testRequestStatus === "FOR_EVALUATION" && purpose !== "APPLICATION_PROCESS"
      ? Yup.date().required("Estimated finishing date is required")
      : Yup.date().nullable();

  const businessUnit = optionalIfCrmLinked(linkedWithOppId, businessUnitErrorMessage);
  const marketSegment = optionalIfCrmLinked(linkedWithOppId, marketSegmentErrorMessage);
  const applicationField = optionalIfCrmLinked(linkedWithOppId, applicationFieldErrorMessage);

  return Yup.object().shape(
    {
      [FieldNames.APPLICATION_FIELD]: applicationField,
      [FieldNames.APPROVER]: Yup.object().nullable(),
      [FieldNames.BACKGROUND_INFORMATION]: Yup.string().matches(/\w/, inputFormatErrorMessage).nullable(),
      [FieldNames.BUSINESS_TYPE]: Yup.string().nullable(),
      [FieldNames.BUSINESS_UNIT]: businessUnit,
      [FieldNames.CONFIDENTIALITY]: Yup.string().required(dropdownRequiredErrorMessage),
      [FieldNames.CRM_NO]: validPositiveInteger().nullable(),
      [FieldNames.CSC_LAB]: Yup.string().required(dropdownRequiredErrorMessage),
      [FieldNames.CURRENCY]: Yup.string().nullable(),
      [FieldNames.OEM]: Yup.string().nullable(),
      [FieldNames.DEADLINE]: Yup.date().nullable(),
      [FieldNames.DEBITOR_NO]: validPositiveInteger().nullable().notRequired(),
      [FieldNames.DIRECT_CUSTOMER]: Yup.object()
        .shape({
          id: Yup.number(),
          name: Yup.string(),
        })
        .required(dropdownRequiredErrorMessage),
      [FieldNames.DIRECT_CUSTOMER_CGR]: Yup.string().nullable(),
      [FieldNames.DIRECT_CUSTOMER_LOCATION]: Yup.object()
        .shape({
          id: Yup.number(),
          name: Yup.string(),
        })
        .required(dropdownRequiredErrorMessage),
      [FieldNames.DISTRIBUTOR]: Yup.string().nullable(),
      [FieldNames.FINISHING_DATE]: estimatedFinishingDate,
      [FieldNames.TEST_EFFORT_IN_DAYS]: testEffortInDays,
      [FieldNames.INTERNAL_CONTACTS]: Yup.array().nullable(),
      [FieldNames.MARKET_SEGMENT]: marketSegment,
      [FieldNames.OPPORTUNITY_PRODUCTS]: Yup.array().nullable(),
      [FieldNames.PNS]: validPositiveBigDecimal().nullable(),
      [FieldNames.PRODUCTS]: Yup.string().nullable(),
      [FieldNames.REPORT_LANGUAGE]:
        purpose === "APPLICATION_PROCESS"
          ? Yup.string().nullable()
          : Yup.string().required(dropdownRequiredErrorMessage),
      [FieldNames.REPORT_USAGE]: Yup.string().nullable(),
      [FieldNames.SOLUTION]: Yup.string().nullable(),
      [FieldNames.TEST_PURPOSE]: testPurpose,
      [FieldNames.SPECIFICATION]: Yup.array().nullable(),
      [FieldNames.SUBSTRATES]: purpose === "APPLICATION_PROCESS" ? Yup.string().nullable() : Yup.string().required(),
      [FieldNames.TEST_LOCATION]: Yup.string().nullable(),
      [FieldNames.TEST_PROGRAM]: Yup.string().nullable(),
      [FieldNames.PRODUCT_REFERENCES]: Yup.array().when(
        [FieldNames.COMPETITOR_PRODUCT_REFERENCES, FieldNames.PROTOTYPE_PRODUCT_REFERENCES],
        {
          is: (
            competitorProductReferences: ProductReferenceResponseWithType[],
            prototypeProductReferences: ProductReferenceResponseWithType[],
          ) => {
            return (
              (competitorProductReferences && competitorProductReferences.length) ||
              (prototypeProductReferences && prototypeProductReferences.length)
            );
          },
          then: Yup.array().min(0),
          otherwise: Yup.array().min(1),
        },
      ),
      [FieldNames.PROTOTYPE_PRODUCT_REFERENCES]: Yup.array().when(
        [FieldNames.PRODUCT_REFERENCES, FieldNames.COMPETITOR_PRODUCT_REFERENCES],
        {
          is: (
            productReferences: ProductReferenceResponse[],
            competitorProductReferences: ProductReferenceResponseWithType[],
          ) => {
            return (
              (productReferences && productReferences.length) ||
              (competitorProductReferences && competitorProductReferences.length)
            );
          },
          then: Yup.array().min(0),
          otherwise: Yup.array().min(1),
        },
      ),
      [FieldNames.COMPETITOR_PRODUCT_REFERENCES]: Yup.array().when(
        [FieldNames.PRODUCT_REFERENCES, FieldNames.PROTOTYPE_PRODUCT_REFERENCES],
        {
          is: (
            productReferences: ProductReferenceResponse[],
            prototypeProductReferences: ProductReferenceResponseWithType[],
          ) => {
            return (
              (productReferences && productReferences.length) ||
              (prototypeProductReferences && prototypeProductReferences.length)
            );
          },
          then: Yup.array().min(0),
          otherwise: Yup.array().min(1),
        },
      ),
    },
    [
      [FieldNames.PRODUCT_REFERENCES, FieldNames.COMPETITOR_PRODUCT_REFERENCES],
      [FieldNames.PRODUCT_REFERENCES, FieldNames.PROTOTYPE_PRODUCT_REFERENCES],
      [FieldNames.PROTOTYPE_PRODUCT_REFERENCES, FieldNames.COMPETITOR_PRODUCT_REFERENCES],
    ],
  );
}
